"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format-datetime";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PROBLEM_DIFFICULTIES } from "../../types";
import type { DifficultyCounts, JudgePracticeProgress } from "../../types";
import { DIFFICULTY_DOT } from "../../lib/format";

export { DIFFICULTY_DOT };

const sum = (counts: DifficultyCounts) => counts.easy + counts.medium + counts.hard;

/**
 * The library at a glance: one bar split by difficulty and its legend. Built
 * from `library`, the whole public catalogue, so it does not move while the
 * reader filters the list below it.
 */
export function LibraryMeter({ library, locale }: { library: DifficultyCounts; locale: string }) {
  const t = useTranslations("judge.catalog");
  const total = sum(library);

  return (
    <div className="mt-7 max-w-[520px]">
      {total > 0 ? (
        <div className="flex h-2 gap-[3px] overflow-hidden rounded-full" aria-hidden>
          {PROBLEM_DIFFICULTIES.map((difficulty) =>
            library[difficulty] > 0 ? (
              <span
                key={difficulty}
                className={cn("h-full rounded-[2px]", DIFFICULTY_DOT[difficulty])}
                style={{ flexGrow: library[difficulty] }}
              />
            ) : null,
          )}
        </div>
      ) : null}
      <ul
        aria-label={t("meterLabel")}
        className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[13.5px] text-neutral-300"
      >
        <li>
          {t.rich("meterTotal", {
            n: total,
            count: formatNumber(total, locale),
            b: (chunks) => <b className="font-bold text-white">{chunks}</b>,
          })}
        </li>
        {PROBLEM_DIFFICULTIES.map((difficulty) => (
          <li key={difficulty} className="inline-flex items-center">
            <i className={cn("mr-2 inline-block h-2 w-2 rounded-[2px]", DIFFICULTY_DOT[difficulty])} aria-hidden />
            {t(`difficulty_${difficulty}`)}
            <b className="ml-1.5 font-bold text-white">{formatNumber(library[difficulty], locale)}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

const RING_RADIUS = 34;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * The head's right-hand panel: the reader's own progress when signed in, the
 * reason to sign in when not. `viewer` is `undefined` while either the session
 * or the list is still settling, and the panel holds its place meanwhile.
 */
export function LibraryProgress({
  locale,
  signedIn,
  viewer,
  library,
}: {
  locale: string;
  signedIn: boolean;
  viewer: JudgePracticeProgress | null | undefined;
  library: DifficultyCounts | undefined;
}) {
  const t = useTranslations("judge.catalog");
  const n = (value: number) => formatNumber(value, locale);
  const signInHref = useSignInHref(locale);

  if (!signedIn) {
    return (
      <div className="rounded-xl border border-white/10 bg-neutral-900 p-5 sm:p-6">
        <p className="text-base font-bold">{t("guestTitle")}</p>
        <p className="mt-2 text-sm leading-relaxed text-neutral-400">{t("guestBody")}</p>
        <Button asChild variant="inverse" className="mt-4 h-10">
          <Link href={signInHref}>{t("signIn")}</Link>
        </Button>
      </div>
    );
  }

  if (!viewer || !library) {
    return (
      <div className="flex items-center gap-5 rounded-xl border border-white/10 bg-neutral-900 p-5 sm:p-6">
        <Skeleton className="h-[84px] w-[84px] shrink-0 rounded-full bg-white/10" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-2 w-full bg-white/10" />
          <Skeleton className="h-2 w-full bg-white/10" />
          <Skeleton className="h-2 w-full bg-white/10" />
        </div>
      </div>
    );
  }

  const solved = sum(viewer.solved);
  const total = sum(library);
  const last = viewer.lastAttempted;
  const lastTitle = last ? (locale === "bn" && last.title_bn ? last.title_bn : last.title_en) : null;

  return (
    <div className="rounded-xl border border-white/10 bg-neutral-900 p-5 sm:p-6">
      <div className="flex items-center gap-[18px]">
        <div
          role="img"
          aria-label={t("ringLabel", { solved: n(solved), total: n(total) })}
          className="relative h-[84px] w-[84px] shrink-0"
        >
          <svg viewBox="0 0 84 84" className="h-[84px] w-[84px] -rotate-90" aria-hidden>
            <circle cx="42" cy="42" r={RING_RADIUS} fill="none" strokeWidth="8" className="stroke-neutral-800" />
            {solved > 0 && total > 0 ? (
              <circle
                cx="42"
                cy="42"
                r={RING_RADIUS}
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                className="stroke-primary"
                strokeDasharray={`${(RING_LENGTH * Math.min(solved, total)) / total} ${RING_LENGTH}`}
              />
            ) : null}
          </svg>
          <span className="absolute inset-0 flex flex-col items-center justify-center leading-tight" aria-hidden>
            <b className="font-mono text-xl font-bold">
              {n(solved)}/{n(total)}
            </b>
            <span className="text-[11px] text-neutral-400">{t("ringSolved")}</span>
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-2.5">
          {PROBLEM_DIFFICULTIES.map((difficulty) => {
            const of = library[difficulty];
            const done = Math.min(viewer.solved[difficulty], of);
            return (
              <div key={difficulty} className="flex items-center gap-2.5 text-[13px] text-neutral-300">
                <span className="w-[62px] shrink-0">{t(`difficulty_${difficulty}`)}</span>
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-800" aria-hidden>
                  <i
                    className={cn("block h-full rounded-full", DIFFICULTY_DOT[difficulty])}
                    style={{ width: of > 0 ? `${(100 * done) / of}%` : "0%" }}
                  />
                </span>
                <span className="w-9 shrink-0 text-right font-mono text-xs text-white">
                  {n(done)}/{n(of)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {viewer.attempted > 0 && last && lastTitle ? (
        <p className="mt-3.5 text-sm leading-relaxed text-neutral-400">
          {t("triedPickUp", { count: n(viewer.attempted) })}{" "}
          <Link
            href={`/${locale}/judge/${last.slug}`}
            className="font-medium text-accent-peach transition-colors duration-200 hover:text-white"
          >
            {lastTitle}
          </Link>
        </p>
      ) : solved === 0 ? (
        <p className="mt-3.5 text-sm leading-relaxed text-neutral-400">{t("firstSolveHint")}</p>
      ) : null}
    </div>
  );
}
